<template>
	<!--
    This component adds pagingation by displaying arrow navigation and a dropdown,
    and sends an event to its parent called pagination:change with an object of
    rows to display.
  -->
	<div class="pagination" v-if="this.data.length > 0">
		<div class="pagination__arrows">
			<button
				@click="prevPage"
				@keydown.enter.prevent="prevPage"
				:class="[firstResult === 1 ? 'disabled' : '']"
				class="icon icon-left"
				:aria-label="`Back one page (currently displaying results ${firstResult} to ${endResult} of ${totalResults})`"
				tabindex="0"
				:title="prevPageBtnTitle ? prevPageBtnTitle : null"
				:aria-disabled="firstResult === 1"
			/>
			<span class="visually-hidden" aria-live="polite" aria-atomic="true">
				Displaying results {{ firstResult }} to {{ endResult }} of
				{{ totalResults }}
			</span>
			<span aria-hidden="true">
				{{ firstResult }}–{{ endResult }} of {{ totalResults }}
			</span>
			<button
				@click="nextPage"
				@keydown.enter.prevent="nextPage"
				:class="[endResult === totalResults ? 'disabled' : '']"
				class="icon icon-right"
				:aria-label="`Forward one page (currently displaying results ${firstResult} to ${endResult} of ${totalResults})`"
				tabindex="0"
				:data-rpa-tag-id="dataRPATagId"
				:title="nextPageBtnTitle ? nextPageBtnTitle : null"
				:aria-disabled="endResult === totalResults"
			/>
		</div>
		<div class="pagination__dropdown" v-if="select.options.length > 1">
			<SecondarySelect
				:options="select.options"
				:invert="true"
				label="Select a page"
				:id="paginationId"
				:selected="currentPage - 1"
				@selected-change-index="updateSelect"
			/>
		</div>
	</div>
</template>

<script>
import { SecondarySelect } from "../select";
import { UniqueId } from "../../../utilities";

/**
 * This component adds pagingation by displaying arrow navigation and a dropdown, and sends an event to its parent called pagination:change with an object of rows to display.
 */
export default {
	name: "Pagination",
	components: { SecondarySelect },
	props: {
		/**
		 * The number of items per page.
		 */
		pageSize: Number,

		/**
		 * An array of objects making up the full set of data. This component will break up the data into pages and return the set to display based on the page selection.
		 */
		data: Array,

		/**
		 * The active page to start on.
		 */
		activePage: {
			type: Number,
			default: 1
		},

		/**
		 * Set an accessibility title for previous page button.
		 */
		prevPageBtnTitle: {
			type: String,
			default: "Previous page"
		},

		/**
		 * Set an accessibility title for next page button.
		 */
		nextPageBtnTitle: {
			type: String,
			default: "Next page"
		},

		/**
		 * Set an rpa tag id for the pagination next button for testing purposes.
		 */
		dataRPATagId: {
			type: String,
			default: ""
		}
	},
	watch: {
		/* update results when page number changes */
		activePage: {
			immediate: true,
			handler(newVal) {
				let page = Number.parseInt(newVal);
				if (page >= 0 && this.currentPage !== page) {
					this.currentPage = page;
				}
			}
		},
		currentPage(newVal) {
			/**
			 * This emits the page number on page change.
			 *
			 * @action
			 */
			this.$emit("pagination:page-num-change", newVal);
			this.getFilteredRows();
		},
		/* reset currentPage and filter new incoming data */
		data() {
			this.currentPage = this.activePage;
			this.getFilteredRows();
		}
	},
	computed: {
		/* Calculate number of first displayed result */
		firstResult() {
			const num = (this.currentPage - 1) * this.pageSize + 1;
			return num < 0 ? 0 : num;
		},
		/* Calculate number of last displayed result */
		endResult() {
			const num =
				(this.currentPage - 1) * this.pageSize + this.paginatedRows.length;
			return num < 0 ? 0 : num;
		},
		/* Calculate total results */
		totalResults() {
			return this.data.length;
		},
		/* Calculate total pages */
		totalPages() {
			return Math.ceil(this.totalResults / this.pageSize);
		},
		/* Populate dropdown based on number of pages */
		select() {
			let temp = {};
			temp.options = [];

			for (let i = 1; i <= this.totalPages; i++) {
				let option = { value: i, label: "Page " + i };
				temp.options.push(option);
			}
			return temp;
		},
		paginationId() {
			return this.id ? this.id : this.uniqueId;
		}
	},
	methods: {
		/* Update currentPage when dropdown changes */
		updateSelect(index) {
			this.currentPage = this.select.options[index].value;
		},
		/* Decrease current page when left arrow clicked */
		prevPage() {
			if (this.firstResult !== 1) this.currentPage--;
		},
		/* Increase current page when right arrow clicked */
		nextPage() {
			if (this.endResult !== this.totalResults) this.currentPage++;
		},
		/* Filter rows of original data based on page number */
		getFilteredRows() {
			this.paginatedRows = this.data.filter((row, index) => {
				const start = (this.currentPage - 1) * this.pageSize;
				const end = this.currentPage * this.pageSize;

				if (index >= start && index < end) {
					return true;
				}
			});

			/**
			 * This emits the rows from the data array to display.
			 *
			 * @action
			 */
			this.$emit("pagination:change", this.paginatedRows);
		}
	},
	/* Set up page one on load */
	created() {
		this.getFilteredRows();
	},
	data() {
		return {
			currentPage: 1,
			paginatedRows: [],
			uniqueId: new UniqueId("pageselect-").generate()
		};
	}
};
</script>

<style lang="scss">
.pagination {
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin-top: 15px;

	@include from-md {
		justify-content: flex-end;
		margin-top: 25px;
	}

	&__arrows {
		align-items: center;
		display: flex;
		transform: translateY(2px);

		@include from-md {
			margin-right: 20px;
		}

		.icon {
			background-color: transparent;
			border: none;
		}

		.icon-left {
			margin-right: 4px;
		}

		.icon-right {
			margin-left: 4px;
		}

		.icon-left,
		.icon-right {
			cursor: pointer;
			font-size: 16px;
			transform: translateY(-2px);

			&.disabled {
				color: $color--light-gray;
				cursor: auto;
			}
		}
	}
	.secondary-select-list {
		top: calc(100% - 2px);
		.secondary-select-option {
			padding-right: 12px;
		}
	}
}
</style>
